import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { api, useAuth, formatApiError } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  ArrowLeft, ChevronDown, ChevronRight, ShieldCheck, Sparkles,
  Wrench, Zap, HeartHandshake, Package, Car, Caravan, Bike, Ship, Loader2,
  FileDown, Link2, ShoppingCart, DollarSign, Copy, Check, Download, Eye, Pencil, Trash2,
} from "lucide-react";
import { RateCardEditor } from "@/components/RateCardEditor";
import { usePdfPreview } from "@/components/PdfPreviewDialog";
import { CombinedRateCard } from "@/components/CombinedRateCard";

const VERTICAL_META = {
  automotive:  { name: "Automotive",  icon: Car,     tagline: "Extended service, ancillary, GAP, and loyalty programs for the auto retailer." },
  rv:          { name: "RV",          icon: Caravan, tagline: "Coverage and ancillary programs tailored for the RV lifestyle retailer." },
  powersports: { name: "Powersports", icon: Bike,    tagline: "Motorcycle, ATV, UTV, and snowmobile program suite." },
  marine:      { name: "Marine",      icon: Ship,    tagline: "Boat and personal-watercraft coverage and ancillary programs." },
};

const CATEGORY_META = {
  vsc:       { label: "VSC",        color: "bg-[#0B132B] text-white",             icon: ShieldCheck },
  "on-road vsc":  { label: "On-Road VSC",  color: "bg-[#0B132B] text-white", icon: ShieldCheck },
  "off-road vsc": { label: "Off-Road VSC", color: "bg-red-800 text-white",   icon: ShieldCheck },
  ancillary: { label: "Ancillary",  color: "bg-[#D4AF37]/15 text-[#0B132B] border border-[#D4AF37]", icon: Sparkles },
  gap:       { label: "GAP",        color: "bg-blue-50 text-blue-800 border border-blue-200",       icon: Package },
  loyalty:   { label: "Loyalty",    color: "bg-purple-50 text-purple-800 border border-purple-200", icon: HeartHandshake },
  none:      { label: "Program",    color: "bg-[#F1F3F5] text-[#0B132B] border border-[#E9ECEF]",    icon: Wrench },
};

const parseNum = (v) => {
  if (v === null || v === undefined || v === "") return null;
  const n = typeof v === "number" ? v : Number(String(v).replace(/[$,]/g, ""));
  return Number.isFinite(n) ? n : null;
};
const fmtMoney = (n) => `$${Number(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtCell = (v, cost) => {
  const n = parseNum(v);
  if (cost && n !== null) return fmtMoney(n);
  return v === null || v === undefined ? "" : String(v);
};

// Persist cart identically to MarketingCatalog.jsx (localStorage key).
const CART_KEY = "awg_marketing_cart";
const readCart = () => { try { return JSON.parse(localStorage.getItem(CART_KEY) || "{}"); } catch (e) { return {}; } };
const writeCart = (c) => localStorage.setItem(CART_KEY, JSON.stringify(c));

// -----------------------------------------------------------------------------
// Rate view — full toolbar (markup, PDF, share, materials, add-to-cart)
// -----------------------------------------------------------------------------
function RateTables({ product }) {
  const productId = product.id;
  const { user } = useAuth();
  const [editOpen, setEditOpen] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [full, setFull] = useState(null);
  const [err, setErr] = useState(null);
  const [pageMarkup, setPageMarkup] = useState(0);
  const [overrides, setOverrides] = useState({}); // {"t.r": markup}
  const [materials, setMaterials] = useState([]);
  const [shareOpen, setShareOpen] = useState(false);
  const [share, setShare] = useState(null);
  const [busyPdf, setBusyPdf] = useState(false);
  const [copied, setCopied] = useState(false);
  const [dealerName, setDealerName] = useState("");
  const [notes, setNotes] = useState("");

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [p, m] = await Promise.all([
          api.get(`/products/${productId}`),
          api.get(`/marketing/materials?product_id=${encodeURIComponent(productId)}`).catch(() => ({ data: [] })),
        ]);
        if (!alive) return;
        setFull(p.data);
        setMaterials(m.data || []);
      } catch (e) {
        if (alive) setErr(e.response?.data?.detail || "Failed to load rates");
      }
    })();
    return () => { alive = false; };
  }, [productId, reloadKey]);

  const buildBody = () => {
    // Group overrides by table -> row so backend can index them.
    const row_overrides = {};
    Object.entries(overrides).forEach(([k, v]) => {
      const [t, r] = k.split(".");
      if (v === "" || v === null || v === undefined || Number.isNaN(Number(v))) return;
      row_overrides[t] = row_overrides[t] || {};
      row_overrides[t][r] = Number(v);
    });
    return {
      product_id: productId,
      page_markup: Number(pageMarkup) || 0,
      row_overrides,
      dealer_name: dealerName || null,
      notes: notes || null,
    };
  };

  const { preview, dialog: pdfDialog } = usePdfPreview();
  const downloadPdf = async () => {
    setBusyPdf(true);
    try {
      await preview({
        fetcher: () => api.post("/portfolio/rate-card/pdf", buildBody(), { responseType: "blob" }),
        filename: `AWG_Rate_Card_${product.slug || product.name}.pdf`,
        title: `Rate Card · ${product.name}`,
      });
    } finally { setBusyPdf(false); }
  };

  const createShare = async () => {
    try {
      const { data } = await api.post("/portfolio/rate-card/share", buildBody());
      setShare(data);
      setShareOpen(true);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
  };

  const copyShareLink = async () => {
    if (!share?.share_url) return;
    try {
      await navigator.clipboard.writeText(share.share_url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
      toast.success("Link copied");
    } catch (e) {
      toast.error("Copy failed — select and copy manually");
    }
  };

  const addAllMaterialsToCart = () => {
    if (materials.length === 0) { toast.error("No marketing materials assigned to this program yet."); return; }
    const c = readCart();
    materials.forEach((m) => { c[m.id] = (c[m.id] || 0) + 1; });
    writeCart(c);
    window.dispatchEvent(new Event("storage"));
    toast.success(`Added ${materials.length} material${materials.length > 1 ? "s" : ""} to cart`);
  };

  const addOneToCart = (m) => {
    const c = readCart();
    c[m.id] = (c[m.id] || 0) + 1;
    writeCart(c);
    window.dispatchEvent(new Event("storage"));
    toast.success(`Added "${m.title}" to cart`);
  };

  const previewMaterial = async (m) => {
    try {
      const { data, headers } = await api.get(`/marketing/materials/${m.id}/file`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data], { type: headers["content-type"] || "application/pdf" }));
      window.open(url, "_blank", "noopener");
    } catch (e) {
      toast.error("Preview failed — file may not be uploaded yet.");
    }
  };

  if (err) return <div className="text-xs text-red-600 py-3">{err}</div>;
  if (!full) return <div className="text-xs text-[#6C757D] py-3 inline-flex items-center gap-2"><Loader2 size={12} className="animate-spin"/> Loading rates…</div>;

  const tables = full.rate_tables || [];

  return (
    <div className="space-y-6 py-4" data-testid={`rate-view-${product.slug}`}>
      {/* Toolbar */}
      <Card className="p-4 border-[#E9ECEF] rounded-sm bg-[#F8F9FA]">
        <div className="flex items-end gap-3 flex-wrap">
          <div>
            <Label className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#6C757D]">Page markup ($ added to every rate)</Label>
            <div className="mt-1 relative">
              <DollarSign size={13} className="absolute left-2 top-2.5 text-[#6C757D]"/>
              <Input
                type="number" min={0} step="1"
                value={pageMarkup}
                onChange={(e) => setPageMarkup(e.target.value === "" ? 0 : Number(e.target.value))}
                className="rounded-sm h-9 w-32 pl-7 text-sm"
                data-testid="rate-page-markup"
              />
            </div>
          </div>
          <div className="flex-1" />
          <Button size="sm" onClick={downloadPdf} disabled={busyPdf}
            className="h-9 rounded-sm bg-[#0B132B] text-white hover:bg-[#0B132B]/90"
            data-testid="rate-generate-pdf-btn">
            {busyPdf ? <><Loader2 className="animate-spin mr-1.5" size={12}/> Generating…</> : <><Eye size={12} className="mr-1.5"/> Preview & Download</>}
          </Button>
          <Button size="sm" variant="outline" onClick={createShare}
            className="h-9 rounded-sm border-[#0B132B]/20"
            data-testid="rate-share-btn">
            <Link2 size={12} className="mr-1.5"/> Create dealer link
          </Button>
          <Button size="sm" variant="outline" onClick={addAllMaterialsToCart}
            className="h-9 rounded-sm border-[#D4AF37] text-[#0B132B]"
            data-testid="rate-add-all-materials-btn">
            <ShoppingCart size={12} className="mr-1.5"/> Add all materials to cart
          </Button>
          {user?.role === "admin" && (
            <Button size="sm" variant="outline" onClick={() => setEditOpen(true)}
              className="h-9 rounded-sm border-[#0B132B]/20"
              data-testid="rate-edit-btn">
              <Pencil size={12} className="mr-1.5"/> Edit rate card
            </Button>
          )}
        </div>
        {user?.role === "admin" && (
          <RateCardEditor productId={productId} open={editOpen} onOpenChange={setEditOpen} onSaved={() => setReloadKey((k) => k + 1)} />
        )}
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#6C757D]">Dealer name (optional)</Label>
            <Input value={dealerName} onChange={(e) => setDealerName(e.target.value)} placeholder="e.g. Smith Motors"
              className="rounded-sm h-9 text-sm mt-1" data-testid="rate-dealer-name"/>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#6C757D]">Notes on rate card (optional)</Label>
            <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Additional terms, disclaimers, etc."
              className="rounded-sm h-9 text-sm mt-1" data-testid="rate-notes"/>
          </div>
        </div>
      </Card>

      {tables.length === 0 ? (
        <div className="text-xs text-[#6C757D] italic py-3">No rate tables published yet for this product.</div>
      ) : tables.map((t, i) => (
        <div key={t.name || `t${i}`} data-testid={`rate-table-${i}`}>
          <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#0B132B] mb-2">{t.name}</div>
          {t.note_inline && <div className="text-[11px] italic text-[#6C757D] mb-1.5" data-testid={`rate-table-note-${i}`}>{t.note_inline}</div>}
          <div className="overflow-x-auto border border-[#E9ECEF] rounded-sm">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-[#0B132B] text-white">
                  {(t.headers || []).map((h, hi) => (
                    <th key={`${t.name}-h${hi}`} className="px-3 py-2 text-left font-bold uppercase tracking-[0.08em]">{h}</th>
                  ))}
                  <th className="px-3 py-2 text-right font-bold uppercase tracking-[0.08em] bg-[#D4AF37]/90 text-[#0B132B] w-32">Row markup</th>
                </tr>
              </thead>
              <tbody>
                {(t.rows || []).map((r, ri) => {
                  const overrideKey = `${i}.${ri}`;
                  const rowMarkup = overrides[overrideKey];
                  const effective = rowMarkup !== undefined && rowMarkup !== "" ? Number(rowMarkup) : Number(pageMarkup || 0);
                  const costIdx = (t.cost_columns || []);
                  return (
                    <tr key={`${t.name}-row-${ri}`} className={ri % 2 ? "bg-[#F8F9FA]" : "bg-white"}>
                      {r.map((c, ci) => {
                        const isCost = costIdx.includes(ci);
                        const base = parseNum(c);
                        const adjusted = isCost && base !== null ? base + effective : c;
                        return (
                          <td key={`${t.name}-r${ri}-c${ci}`} className={`px-3 py-1.5 border-t border-[#E9ECEF] ${isCost ? "text-right font-bold text-[#0B132B]" : "text-[#0B132B]"}`}>
                            {fmtCell(adjusted, isCost)}
                          </td>
                        );
                      })}
                      <td className="px-2 py-1 border-t border-[#E9ECEF] border-l-2 border-l-[#D4AF37]/40 bg-[#D4AF37]/5 w-32 text-right">
                        <Input
                          type="number" step="1"
                          placeholder={pageMarkup ? `+${pageMarkup}` : "0"}
                          value={rowMarkup ?? ""}
                          onChange={(e) => setOverrides({ ...overrides, [overrideKey]: e.target.value })}
                          className="rounded-sm h-7 text-xs text-right px-1.5"
                          data-testid={`rate-row-markup-${i}-${ri}`}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ))}

      {/* Marketing materials for this program */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#0B132B]">Program materials</div>
          <div className="text-[10px] text-[#6C757D]">{materials.length} available</div>
        </div>
        {materials.length === 0 ? (
          <Card className="p-4 border-dashed border-[#E9ECEF] text-center text-xs text-[#6C757D]" data-testid="rate-materials-empty">
            No brochures, laminates, or sample contracts assigned to this program yet. Admins can upload via Materials.
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2" data-testid="rate-materials-list">
            {materials.map((m) => (
              <Card key={m.id} className="p-3 border-[#E9ECEF] rounded-sm bg-white flex items-center gap-3" data-testid={`rate-material-${m.id}`}>
                <Package size={14} className="text-[#D4AF37] shrink-0"/>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-bold text-[#0B132B] truncate">{m.title}</div>
                  <div className="text-[10px] uppercase tracking-wider text-[#6C757D]">{m.category?.replace("_", " ")}</div>
                </div>
                {m.has_file && (
                  <Button size="icon" variant="outline" onClick={() => previewMaterial(m)}
                    className="h-8 w-8 rounded-sm border-[#0B132B]/20" title="Preview"
                    data-testid={`rate-material-preview-${m.id}`}>
                    <Eye size={12}/>
                  </Button>
                )}
                <Button size="sm" onClick={() => addOneToCart(m)}
                  className="h-8 rounded-sm bg-[#0B132B] text-white hover:bg-[#0B132B]/90"
                  data-testid={`rate-material-cart-${m.id}`}>
                  <ShoppingCart size={11} className="mr-1"/> Cart
                </Button>
              </Card>
            ))}
          </div>
        )}
      </div>

      {full.rate_source_filename && (
        <div className="text-[10px] text-[#6C757D]">
          Source: {full.rate_source_filename}
          {full.rate_source_uploaded_at ? ` · uploaded ${new Date(full.rate_source_uploaded_at).toLocaleDateString()}` : ""}
        </div>
      )}

      {/* Share dialog */}
      <Dialog open={shareOpen} onOpenChange={setShareOpen}>
        <DialogContent data-testid="rate-share-dialog">
          <DialogHeader><DialogTitle>Public rate card link</DialogTitle></DialogHeader>
          <p className="text-sm text-[#6C757D]">
            Share this URL with your dealer. It shows the rates with your markup applied and lets them download the PDF — no sign-in required.
          </p>
          <div className="flex items-center gap-2 p-2 bg-[#F8F9FA] rounded-sm border border-[#E9ECEF]">
            <input readOnly value={share?.share_url || ""} className="flex-1 bg-transparent text-xs font-mono outline-none" data-testid="rate-share-url"/>
            <Button size="sm" variant="outline" onClick={copyShareLink} className="h-8 rounded-sm" data-testid="rate-share-copy-btn">
              {copied ? <Check size={12} className="mr-1 text-green-600"/> : <Copy size={12} className="mr-1"/>}
              {copied ? "Copied" : "Copy"}
            </Button>
          </div>
          <DialogFooter>
            {share?.share_url && (
              <a href={`mailto:?subject=${encodeURIComponent(`Rate Card — ${product.name}`)}&body=${encodeURIComponent(`Here's the rate card for ${product.name}:\n\n${share.share_url}`)}`}
                className="inline-flex items-center gap-1.5 h-9 px-4 rounded-sm bg-[#0B132B] text-white text-sm font-bold hover:bg-[#0B132B]/90"
                data-testid="rate-share-email-btn">
                Email to dealer
              </a>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {pdfDialog}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Product card — collapsed by default; click to reveal rate tables + toolbar
// -----------------------------------------------------------------------------
function ProductCard({ product, onRemoved }) {
  const [open, setOpen] = useState(false);
  const { user } = useAuth();
  const meta = CATEGORY_META[product.category] || CATEGORY_META.none;
  const CatIcon = meta.icon;
  const remove = async (e) => {
    e.stopPropagation();
    if (!window.confirm(`Remove "${product.name}" from the rate card generator? This deletes the card and its admin fee. Re-upload a rate sheet to bring it back.`)) return;
    try { await api.delete(`/admin/products/${product.id}`); toast.success(`${product.name} removed`); onRemoved?.(product.id); }
    catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); }
  };
  return (
    <Card className="border-[#E9ECEF] rounded-sm overflow-hidden" data-testid={`product-card-${product.slug}`}>
      <div className="flex items-start">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex-1 min-w-0 text-left p-5 flex items-start gap-4 hover:bg-[#F8F9FA] transition-colors"
        aria-expanded={open}
        data-testid={`product-toggle-${product.slug}`}
      >
        <div className="w-10 h-10 rounded-sm bg-[#0B132B]/5 border border-[#0B132B]/10 flex items-center justify-center shrink-0">
          <CatIcon size={16} className="text-[#0B132B]"/>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className={`text-[9px] uppercase tracking-[0.16em] font-bold px-2 py-0.5 rounded-sm ${meta.color}`}>
              {meta.label}
            </span>
            {!product.active && (
              <span className="text-[9px] uppercase tracking-[0.16em] font-bold px-2 py-0.5 rounded-sm bg-red-50 text-red-700 border border-red-200">
                Inactive
              </span>
            )}
            {product.has_rates === false && (
              <span className="text-[9px] uppercase tracking-[0.16em] font-bold px-2 py-0.5 rounded-sm bg-amber-50 text-amber-800 border border-amber-200" data-testid={`product-no-rates-${product.slug}`}>
                No rates loaded
              </span>
            )}
          </div>
          <div className="font-bold text-[#0B132B] text-base">{product.name}</div>
          {product.description && (
            <p className="text-sm text-[#6C757D] mt-1 leading-relaxed">{product.description}</p>
          )}
        </div>
        <div className="text-[#6C757D] shrink-0 mt-1">
          {open ? <ChevronDown size={18}/> : <ChevronRight size={18}/>}
        </div>
      </button>
      {user?.role === "admin" && (
        <button type="button" onClick={remove} className="p-2 m-3 rounded-sm text-[#6C757D] hover:text-red-700 hover:bg-red-50 shrink-0" title="Remove from generator" data-testid={`product-remove-${product.slug}`}>
          <Trash2 size={14} />
        </button>
      )}
      </div>
      {open && (
        <div className="px-5 border-t border-[#E9ECEF] bg-white">
          <RateTables product={product} />
        </div>
      )}
    </Card>
  );
}

// -----------------------------------------------------------------------------
// Vertical page — /portfolio/:slug
// -----------------------------------------------------------------------------
export default function PortfolioVertical() {
  const { slug } = useParams();
  const [products, setProducts] = useState(null);
  const meta = VERTICAL_META[slug] || { name: slug, icon: Package, tagline: "" };
  const Icon = meta.icon;

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/products?vertical=${encodeURIComponent(slug)}`);
        if (alive) setProducts(data);
      } catch (e) {
        if (alive) {
          toast.error("Failed to load products");
          setProducts([]);
        }
      }
    })();
    return () => { alive = false; };
  }, [slug]);

  const grouped = useMemo(() => {
    const buckets = { vsc: [], "on-road vsc": [], "off-road vsc": [], gap: [], ancillary: [], loyalty: [], other: [] };
    (products || []).forEach((p) => {
      const cat = p.category || "";
      if (buckets[cat]) buckets[cat].push(p);
      else buckets.other.push(p);
    });
    return buckets;
  }, [products]);

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <Navbar />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-10" data-testid={`portfolio-vertical-${slug}`}>
        <Link to="/dashboard" className="inline-flex items-center gap-2 text-sm text-[#6C757D] hover:text-[#0B132B] mb-4">
          <ArrowLeft size={14}/> Back to dashboard
        </Link>
        <div className="flex items-start gap-4 mb-10">
          <div className="w-14 h-14 rounded-sm bg-[#D4AF37]/15 border border-[#D4AF37] flex items-center justify-center shrink-0">
            <Icon size={26} className="text-[#0B132B]"/>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold">Portfolio</div>
            <h1 className="font-display text-4xl sm:text-5xl font-black text-[#0B132B] tracking-tight leading-none mt-2">
              {meta.name}
            </h1>
            {meta.tagline && (
              <p className="text-sm text-[#6C757D] mt-3 max-w-2xl leading-relaxed">{meta.tagline}</p>
            )}
          </div>
        </div>

        {products === null ? (
          <div className="text-center text-sm text-[#6C757D] py-20 inline-flex items-center gap-2 justify-center w-full">
            <Loader2 size={14} className="animate-spin"/> Loading products…
          </div>
        ) : products.length === 0 ? (
          <Card className="p-10 border-[#E9ECEF] rounded-sm text-center text-sm text-[#6C757D]" data-testid="portfolio-empty-state">
            No products migrated yet for this vertical. A super admin can seed it from Admin → Migration.
          </Card>
        ) : (
          <div className="space-y-10">
            {Object.entries(grouped).map(([cat, list]) => list.length > 0 && (
              <section key={cat} data-testid={`portfolio-section-${cat}`}>
                <h2 className="text-xs uppercase tracking-[0.22em] font-bold text-[#0B132B] mb-3 flex items-center gap-2">
                  <Zap size={12} className="text-[#D4AF37]"/>
                  {(CATEGORY_META[cat] || CATEGORY_META.none).label} · {list.length}
                </h2>
                <div className="space-y-3">
                  {(() => {
                    const tiers = cat === "vsc" ? list.filter((p) => p.slug?.startsWith("newcar-") && p.slug !== "newcar-class-guide") : [];
                    const combined = tiers.length > 1;
                    // When the combined Essentials New Car card is shown, the individual tier cards are redundant
                    const visible = combined ? list.filter((p) => !tiers.includes(p)) : list;
                    return (<>
                      {combined && <CombinedRateCard title="Essentials New Car" products={tiers} testId="combined-newcar" />}
                      {visible.map((p) => <ProductCard key={p.id} product={p} onRemoved={(id) => setProducts((ps) => ps.filter((x) => x.id !== id))} />)}
                    </>);
                  })()}
                </div>
              </section>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
